<template>
  <div class="location-map-picker">
    <div class="location-map-picker__search">
      <input
        type="text"
        dir="auto"
        v-model="searchQuery"
        @keydown.enter.prevent="search"
        :placeholder="$t('searchForCity')"
        class="location-map-picker__search-input"
      />
      <button
        type="button"
        class="location-map-picker__search-btn"
        :disabled="searching"
        @click="search"
      >
        {{ searching ? "..." : $t("search") }}
      </button>
      <button
        type="button"
        class="location-map-picker__locate-btn"
        @click="locateMe"
        :title="$t('useMyLocation')"
      >
        📍
      </button>
    </div>
    <div v-if="locationNotice" class="location-map-picker__notice">
      {{ locationNotice }}
    </div>
    <ul v-if="searchResults.length" class="location-map-picker__results">
      <li
        v-for="result in searchResults"
        :key="result.place_id"
        @click="selectResult(result)"
      >
        {{ result.display_name }}
      </li>
    </ul>
    <div v-else-if="searched && !searching" class="location-map-picker__no-results">
      {{ $t("noSearchResults") }}
    </div>
    <div ref="mapContainer" class="location-map-picker__map"></div>
    <div class="location-map-picker__hint">📍 {{ $t("clickMapToSelectLocation") }}</div>
    <div class="location-map-picker__manual">
      <div class="location-map-picker__manual-field">
        <label>{{ $t("latitude") }}</label>
        <input
          type="number"
          step="any"
          min="-90"
          max="90"
          v-model="manualLat"
          @change="applyManualCoords"
        />
      </div>
      <div class="location-map-picker__manual-field">
        <label>{{ $t("longitude") }}</label>
        <input
          type="number"
          step="any"
          min="-180"
          max="180"
          v-model="manualLng"
          @change="applyManualCoords"
        />
      </div>
      <span v-if="value && value.lat && value.lng" class="location-map-picker__clear" @click="clear">
        {{ $t("clear") }}
      </span>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Vue CLI's webpack build breaks Leaflet's default marker icon URLs — the
// well-known fix is to re-point them at the bundled asset URLs explicitly.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const DEFAULT_CENTER = { lat: 32.8872, lng: 13.1913 }; // Tripoli
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

export default {
  name: "LocationMapPicker",
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      map: null,
      marker: null,
      searchQuery: "",
      searchResults: [],
      searching: false,
      searched: false,
      locationNotice: null,
      manualLat: this.value && this.value.lat ? this.value.lat : "",
      manualLng: this.value && this.value.lng ? this.value.lng : "",
    };
  },
  watch: {
    searchQuery() {
      // Clear stale results/"no results" state as soon as the query changes
      // so they don't linger over an edited, not-yet-searched query.
      this.searchResults = [];
      this.searched = false;
    },
    // Keep the manual lat/lng inputs mirroring the pin whenever it moves for
    // any other reason (map click, drag, search result, "use my location").
    value(newVal) {
      this.manualLat = newVal && newVal.lat ? newVal.lat : "";
      this.manualLng = newVal && newVal.lng ? newVal.lng : "";
    },
  },
  mounted() {
    const center = this.value && this.value.lat && this.value.lng ? this.value : DEFAULT_CENTER;

    this.map = L.map(this.$refs.mapContainer).setView([center.lat, center.lng], this.value ? 15 : 12);

    // Leaflet's own drag handler still overrides this to a grab/grabbing
    // hand cursor the moment an actual pan starts (higher CSS specificity),
    // so this crosshair only shows while idle — signalling "click to place
    // the pin here" without it fighting the pan cursor.
    L.DomUtil.addClass(this.map._container, "leaflet-crosshair");

    // Satellite imagery reads far more accurately than OSM's street-map
    // tiles for this region (sparser OSM data coverage) — a reference layer
    // with place/road labels is layered on top since raw imagery has none.
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles &copy; Esri — Esri, Maxar, Earthstar Geographics, and the GIS User Community",
        maxZoom: 19,
      }
    ).addTo(this.map);
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19 }
    ).addTo(this.map);

    if (this.value && this.value.lat && this.value.lng) {
      this.setMarker(this.value.lat, this.value.lng);
    } else if (navigator.permissions && navigator.permissions.query) {
      // Only auto-request the device location if permission was already
      // granted. Calling getCurrentPosition unconditionally on every mount
      // re-prompts users who haven't decided yet, and repeatedly dismissing
      // that prompt makes Chrome auto-block geolocation for the site.
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          if (status.state === "granted") {
            this.locateMe(true);
          } else if (status.state === "denied") {
            // Permission is already blocked: don't call getCurrentPosition
            // (that would just re-trigger the browser's blocked-permission
            // console noise for nothing), but tell the user why the map
            // isn't centered on them instead of failing silently.
            this.locationNotice = this.$t("locationPermissionBlocked");
          }
        })
        .catch(() => {});
    } else {
      // Permissions API unavailable: fall back to the old best-effort call.
      // Never blocks/alerts on denial — the map just stays at the default center.
      this.locateMe(true);
    }

    this.map.on("click", (e) => {
      this.locationNotice = null;
      this.setMarker(e.latlng.lat, e.latlng.lng);
      this.$emit("input", { lat: e.latlng.lat, lng: e.latlng.lng });
    });

    // The map is often mounted while its parent is still animating/hidden
    // (v-if toggling) or before sibling form fields (address dropdown, etc.)
    // finish loading and settle the layout, so its container can resize
    // after init — a fixed one-shot timeout can miss a later resize and
    // leave stale, half-rendered tiles. Watch the container instead.
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this.refreshMapSize());
      this._resizeObserver.observe(this.$refs.mapContainer);
    } else {
      setTimeout(() => this.refreshMapSize(), 200);
    }
    // Cover the very first paint too: the container can still report a
    // wrong (e.g. halved) size at the instant L.map() runs, before the
    // browser has finished laying out this component's siblings.
    requestAnimationFrame(() => this.refreshMapSize());
    // Belt-and-suspenders: a plain delayed re-check regardless of
    // ResizeObserver support, for layout settling that doesn't change the
    // container's own box size (e.g. sibling content loading in late)
    // and so wouldn't trigger the observer above.
    setTimeout(() => this.refreshMapSize(), 300);
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    refreshMapSize() {
      if (!this.map) return;
      // Plain invalidateSize() alone can leave half the tile grid stuck
      // blank when the container's measured size was wrong at some point
      // (the classic symptom: the map visually split in half, one side
      // rendered, the other side empty grey). Re-centering on the map's own
      // current center right after forces Leaflet to fully recompute pixel
      // origins and refetch every tile for the now-correct container size.
      this.map.invalidateSize(false);
      this.map.setView(this.map.getCenter(), this.map.getZoom(), { animate: false });
    },
    setMarker(lat, lng) {
      if (this.marker) {
        this.marker.setLatLng([lat, lng]);
      } else {
        this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map);
        this.marker.on("dragend", () => {
          const pos = this.marker.getLatLng();
          this.$emit("input", { lat: pos.lat, lng: pos.lng });
        });
      }
    },
    clear() {
      if (this.marker) {
        this.map.removeLayer(this.marker);
        this.marker = null;
      }
      this.$emit("input", null);
    },
    // silent=true (auto-run on mount): only recenters, never drops a pin or
    // emits a value. silent=false (📍 button): also drops the pin at the
    // device location and emits it. Both surface failures as an inline
    // notice (not window.alert, which is easy to miss/dismiss unnoticed).
    locateMe(silent = false) {
      this.locationNotice = null;
      if (!navigator.geolocation) {
        this.locationNotice = this.$t("locationNotSupported");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.map.setView([lat, lng], 15);
          if (!silent) {
            this.setMarker(lat, lng);
            this.$emit("input", { lat, lng });
          }
          // Browsers can fall back to coarse network/IP-based positioning
          // that's off by tens of kilometers even with high accuracy
          // requested. Warn so the user knows to double check/drag the pin
          // instead of trusting an obviously imprecise fix.
          if (position.coords.accuracy && position.coords.accuracy > 5000) {
            this.locationNotice = this.$t("locationInaccurate");
          }
        },
        (error) => {
          this.locationNotice =
            error.code === error.PERMISSION_DENIED
              ? this.$t("locationPermissionDenied")
              : this.$t("locationUnavailable");
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },
    async search() {
      if (!this.searchQuery.trim()) return;
      this.searching = true;
      this.searched = false;
      try {
        // Deliberately plain `fetch`, not the app's shared axios instance —
        // that instance carries the client's Bearer auth header in its
        // defaults, which would otherwise leak to this third-party host.
        // Restricted to Libya (countrycodes=ly) since that's the only place
        // the business operates — an unrestricted worldwide search buries
        // the relevant local result under unrelated matches from elsewhere.
        const url = `${NOMINATIM_URL}?q=${encodeURIComponent(
          this.searchQuery
        )}&format=json&limit=5&countrycodes=ly&accept-language=${this.$i18n.locale}`;
        const response = await fetch(url);
        this.searchResults = await response.json();
      } catch (e) {
        this.searchResults = [];
      } finally {
        this.searching = false;
        this.searched = true;
      }
    },
    applyManualCoords() {
      const lat = parseFloat(this.manualLat);
      const lng = parseFloat(this.manualLng);
      if (Number.isNaN(lat) || Number.isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        this.locationNotice = this.$t("invalidCoordinates");
        return;
      }
      this.locationNotice = null;
      this.map.setView([lat, lng], 15);
      this.setMarker(lat, lng);
      this.$emit("input", { lat, lng });
    },
    selectResult(result) {
      this.locationNotice = null;
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      this.map.setView([lat, lng], 15);
      this.setMarker(lat, lng);
      this.$emit("input", { lat, lng });
      this.searchResults = [];
      this.searchQuery = result.display_name;
    },
  },
};
</script>

<style lang="scss" scoped>
.location-map-picker {
  position: relative;
  &__search {
    display: flex;
    gap: 6px;
    margin-bottom: 8px;
  }
  &__search-input {
    flex: 1;
    min-width: 0;
    height: 38px;
    padding: 0 10px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    font-size: 0.75rem;
  }
  &__search-btn,
  &__locate-btn {
    height: 38px;
    padding: 0 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    background: #fff;
    font-size: 0.75rem;
    cursor: pointer;
    white-space: nowrap;
  }
  &__results {
    list-style: none;
    margin: -4px 0 8px;
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    max-height: 160px;
    overflow-y: auto;
    li {
      padding: 6px 10px;
      font-size: 0.75rem;
      cursor: pointer;
      &:hover {
        background: #f5f5f5;
      }
      & + li {
        border-top: 1px solid rgba(0, 0, 0, 0.08);
      }
    }
  }
  &__no-results {
    margin: -4px 0 8px;
    padding: 6px 10px;
    font-size: 0.75rem;
    color: #747474;
  }
  &__notice {
    margin-bottom: 8px;
    padding: 8px 10px;
    border: 1px solid #ffd591;
    background: #fff7e6;
    border-radius: 5px;
    font-size: 0.75rem;
    color: #a15c00;
    line-height: 1.5;
  }
  &__map {
    width: 100%;
    height: 260px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    // The real fix for Leaflet's tiles/panes rendering shifted under RTL is
    // the unscoped override below (postcss-rtl mirroring leaflet.css). This
    // is just a harmless belt-and-suspenders for bidi text rendering.
    direction: ltr;
    // Leaflet's internal panes use z-index up to ~700 (tiles, markers,
    // popups). Without this, they don't just stack within the map: since
    // nothing here establishes a stacking context, those z-index values
    // compete directly against unrelated page elements too — e.g. painting
    // over an open datepicker calendar dropdown elsewhere on the same page,
    // even though it's nowhere near the map in the DOM. `isolation: isolate`
    // contains Leaflet's stacking to just this element, regardless of its
    // own `position` value.
    isolation: isolate;
  }
  &__hint {
    font-size: 0.75rem;
    font-weight: 600;
    color: #555;
    margin-top: 6px;
  }
  &__manual {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  &__manual-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    label {
      font-size: 0.7rem;
      color: #747474;
    }
    input {
      width: 140px;
      height: 34px;
      padding: 0 8px;
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 5px;
      font-size: 0.75rem;
      direction: ltr;
      text-align: right;
    }
  }
  &__clear {
    color: #ff4757;
    cursor: pointer;
    font-weight: bold;
    margin-inline-start: 8px;
  }
}
</style>

<style lang="scss">
// Unscoped on purpose: Leaflet builds its tiles/panes/markers with plain
// DOM APIs, so they never get this SFC's scoped data-v-* attribute and a
// scoped selector here would never match them.
//
// The actual root cause of the map rendering shifted/half-blank — on
// initial load AND on every pan/zoom — is this project's postcss.config.js
// running every stylesheet through postcss-rtl, including leaflet.css.
// leaflet.css anchors every pane/tile/marker with `left: 0`, and
// postcss-rtl mirrors that to `right: 0` under `[dir="rtl"]` (this whole
// app's root direction). Leaflet's JS then layers its own pixel-offset
// `transform: translate3d()` math on top, which assumes the left-anchored
// original — so under this app's RTL, every tile ends up anchored from
// the wrong edge, which is what produces the shifted/blank-half symptom
// regardless of container size or resize timing.
//
// Critical: postcss-rtl also runs on THIS override itself and would mirror
// `left: 0 !important` into a `[dir="rtl"] .leaflet-pane { right: 0 !important }`
// variant — which, being an attribute-qualified selector, has HIGHER
// specificity than a plain `.leaflet-pane` rule and would win the cascade,
// silently reintroducing the exact bug this is meant to fix.
//
// A `/* rtl:ignore */` comment is postcss-rtl's documented escape hatch for
// this, but it turned out NOT to survive this project's production build
// pipeline (verified: the deployed and a local `npm run dist` build both
// still contained the mirrored `[dir="rtl"] .leaflet-pane { right: 0 }`
// rule beating this fix, even with the comment present in source — likely
// stripped before postcss-rtl runs, or by the separate CSS minifier).
//
// So instead every selector below already includes a bare `[dir]` attribute
// selector itself. postcss-rtl detects that a selector already references
// `dir` and skips mirroring it entirely (verified directly against this
// project's postcss-rtl version) — no mirrored variant is ever generated,
// so there's nothing for the cascade to fight. `html[dir]` also happens to
// raise this rule's specificity above the mirrored `[dir="rtl"] .foo`
// rule's, as a second, independent line of defense if that skip behavior
// ever changes in a future postcss-rtl version.
html[dir] .leaflet-pane,
html[dir] .leaflet-tile,
html[dir] .leaflet-marker-icon,
html[dir] .leaflet-marker-shadow,
html[dir] .leaflet-tile-container,
html[dir] .leaflet-pane > svg,
html[dir] .leaflet-pane > canvas,
html[dir] .leaflet-zoom-box,
html[dir] .leaflet-image-layer,
html[dir] .leaflet-layer {
  left: 0 !important;
  right: auto !important;
  top: 0 !important;
}

// Same mirroring hits the zoom control's corner container (visible in
// screenshots as the +/- buttons landing top-right instead of top-left).
html[dir] .leaflet-left {
  left: 0 !important;
  right: auto !important;
  float: left !important;
}
html[dir] .leaflet-right {
  right: 0 !important;
  left: auto !important;
  float: right !important;
}

// Not exercised yet (this component doesn't open any L.popup()), but kept
// here so Arabic popup content added later renders correctly by default
// instead of inheriting the ltr fixes above.
.leaflet-popup-content-wrapper,
.leaflet-popup-content {
  direction: rtl;
  text-align: right;
}
</style>

